// CreatePropertyType.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createPropertyType,
  getAllPropertyTypes,
  getPropertyTypeByID,
  propertyTypeImageUpload,
  updatePropertyType,
} from "./services/propertyType.service";
import { clearPropertyTypeDetails } from "./propertyTypeSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getFile } from "../../_helper/helper";

const CreatePropertyType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const PropertyTypeID = useAppSelector((state) => state.drawer.data);
  const PropertyTypeDetail = useAppSelector(
    (state) => state.propertyType.PropertyTypeDetail,
  );
  const loading = useAppSelector((state) => state.propertyType.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PropertyTypeDetail]);

  useEffect(() => {
    if (PropertyTypeID) {
      dispatch(getPropertyTypeByID(PropertyTypeID));
    }
    return () => {
      dispatch(clearPropertyTypeDetails());
    };
  }, [PropertyTypeID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const ImageUpload =
      values?.ImageUpload && typeof values?.ImageUpload !== "string"
        ? getFile(values?.ImageUpload[0])
        : "";

    try {
      if (PropertyTypeID) {
        const editPayload = {
          ...formData,
          ID: PropertyTypeID,
        };

        const response = await dispatch(updatePropertyType(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (values?.ImageUpload && typeof values?.ImageUpload !== "string") {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", PropertyTypeID);
            await dispatch(propertyTypeImageUpload(ImageData));
          }
          dispatch(getAllPropertyTypes());
          dispatch(clearPropertyTypeDetails());
        }
      } else {
        const response = await dispatch(createPropertyType(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.ImageUpload &&
            typeof values?.ImageUpload !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(propertyTypeImageUpload(ImageData));
          }
          dispatch(getAllPropertyTypes());
          dispatch(clearPropertyTypeDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={PropertyTypeDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {PropertyTypeID
                      ? "Update Property Type"
                      : "Add Property Type"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Property Type Title"
                    maxLength="100"
                    placeholder="Enter Property Type title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name="ImageUpload"
                    label="Property Type Image"
                    type="file"
                    alternateName="ImageUpload"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={PropertyTypeID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreatePropertyType;
